import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import { BlishAPI } from '@/apis';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Swords } from 'lucide-react';

export default function ChallengeInviteModal() {
  const navigate = useNavigate();
  const user = useStore((s) => s.user);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    if (!user) return;
    const check = async () => {
      try {
        const { data } = await BlishAPI.challenges.myHistory();
        const pending = data.find(
          (c) => c.status === 'pending' && c.opponent_id === user.id && !dismissed.has(c.id)
        );
        if (pending) setInvite(pending);
      } catch {}
    };
    check();
    const interval = setInterval(check, 10000);
    return () => clearInterval(interval);
  }, [user, dismissed]);

  const handleAccept = async () => {
    if (!invite) return;
    setLoading(true);
    try {
      const { data } = await BlishAPI.challenges.join(invite.id);
      setInvite(null);
      if (data.matchId) {
        navigate(`/play/${data.gameType?.toLowerCase() || invite.game.toLowerCase()}/${data.matchId}`);
      } else {
        toast.success('Challenge accepted!');
        setInvite(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to accept challenge');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!invite) return;
    setLoading(true);
    try {
      await BlishAPI.challenges.reject(invite.id);
      toast.info('Challenge declined');
      setDismissed((prev) => new Set(prev).add(invite.id));
      setInvite(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to decline challenge');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!invite} onOpenChange={(open) => { if (!open) { setDismissed((prev) => new Set(prev).add(invite?.id)); setInvite(null); } }}>
      <DialogContent className="bg-[#1a1a1a] border-gray-800 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Swords className="w-5 h-5 text-lime" />
            Challenge Invite
          </DialogTitle>
          <DialogDescription className="text-muted">
            {invite?.creator_gamertag || 'Someone'} challenged you to <strong>{invite?.game}</strong>
            {invite?.amount > 0 ? ` for ${invite.amount} BC` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 mt-2">
          <Button onClick={handleAccept} disabled={loading} className="flex-1 bg-lime text-[#1a1a1a] hover:bg-lime/90">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Accept
          </Button>
          <Button onClick={handleReject} disabled={loading} variant="outline" className="flex-1 border-gray-700 text-muted hover:text-white">
            Decline
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
